/**
 * Risk Preview Report (AGD-385 / AGD-386).
 *
 * A dataset-wide, point-in-time sweep that computes a user-selected set of
 * *system* risks (the whole catalog, not just the risks configured for the
 * current dataset) and emits an Excel file. It writes nothing to the corpus.
 *
 * Flow: pick risks -> POST /api/v3/datasets/{crd}/risk-report -> poll
 * GET /api/v3/datasets/{crd}/risk-report/{report_id} -> download when ready.
 */

/** A selectable risk from the system catalog (mirrors crd-api SystemRiskResponseSchema). */
export interface SystemRisk {
  id: number;
  name: string;
  display_name: string;
  description: string | null;
  color: string;
  type: string | null;
  category: string | null;
  category_display: string | null;
  // Configured on every dataset and never removable. Optional because older
  // crd-api builds do not send it.
  is_compulsory?: boolean;
}

export interface RiskReportJobResponse {
  report_id: string;
  status: string;
}

export interface RiskReportResult {
  download_url?: string;
  error?: string;
}

export interface RiskReportPollResponse {
  report_id: string;
  status: string;
  percentage: number;
  result: RiskReportResult | null;
}

export const RISK_PREVIEW_REPORT_CONSTANTS = {
  COMPUTABLE_COMPUTE_SOURCE: 'agd-geospatial',
  TERMINAL_STATUSES: ['completed', 'failed'] as const,
  FAILED_STATUS: 'failed',
  POLL_INITIAL_DELAY_MS: 3000,
  // A report can legitimately run for hours, so polling continues until the
  // backend reports a terminal status (it guarantees every run eventually
  // does). Poll fast at first (small reports finish in minutes), then back
  // off.
  POLL_FAST_INTERVAL_MS: 5000,
  POLL_FAST_ATTEMPTS: 24,
  POLL_SLOW_INTERVAL_MS: 30000,
  // The run row is created asynchronously after the POST; a 404 is tolerated
  // only this many polls before it is treated as a real failure. Once the
  // report has responded at least once, any later error (404, 5xx blip,
  // network drop, request timeout) is treated as transient and polling
  // continues — the safety ceiling bounds the worst case.
  POLL_NOT_FOUND_GRACE_ATTEMPTS: 12,
  // The status request goes out with skip-error-interceptor and has no other
  // timeout; without this cap a request that hangs (rather than errors) would
  // block the poll loop until the safety ceiling.
  POLL_REQUEST_TIMEOUT_MS: 20000,
  // Backstop against a run that never reports a terminal status (the backend
  // watchdog should guarantee one within ~45 min): far above any legitimate
  // run time, so hitting it means the run is wedged — stop, clear the
  // loading state, and surface the timeout message.
  POLL_SAFETY_CEILING_MS: 3 * 60 * 60 * 1000
} as const;
